/**
 * Kit IO Excel/CSV (F3-J) — helpers de transporte del wizard y los botones de
 * export. Los endpoints JSON viajan por el cliente tipado (`api`); el multipart
 * de subida y las descargas de attachments usan `ioFetchRaw` (misma auth).
 */

import { ioFetchRaw } from "@/lib/api";
import type { components } from "@space/shared";

export type IoEntity = components["schemas"]["IoEntityDto"];
export type IoEntitiesResponse = components["schemas"]["IoEntitiesResponse"];
export type ImportJobResponse = components["schemas"]["ImportJobResponse"];
export type ImportRowIssue = components["schemas"]["ImportRowIssue"];
export type MappingProposal = components["schemas"]["MappingProposal"];

export const ioQueryKeys = {
  entities: (orgId: string | undefined) => ["io", "entities", orgId ?? "platform"] as const,
  job: (id: string | undefined) => ["io", "import", id ?? "none"] as const,
  issues: (id: string, page: number) => ["io", "import", id, "issues", page] as const,
};

/** Sube el fichero y crea el job (multipart {entityKey, orgId?, file}). */
export async function uploadImportFile(
  entityKey: string,
  orgId: string | undefined,
  file: File,
): Promise<ImportJobResponse> {
  const form = new FormData();
  form.append("entityKey", entityKey);
  if (orgId) {
    form.append("orgId", orgId);
  }
  // Sin Content-Type manual: el navegador añade el boundary del multipart.
  form.append("file", file);
  const res = await ioFetchRaw("/api/io/import", { method: "POST", body: form });
  if (!res.ok) {
    throw new Error(await extractError(res));
  }
  const data: unknown = await res.json();
  if (!isImportJobResponse(data)) {
    throw new Error("Unexpected import response shape");
  }
  return data;
}

/** Type guard mínimo de la respuesta del server (evita asserts inseguros). */
function isImportJobResponse(v: unknown): v is ImportJobResponse {
  return typeof v === "object" && v !== null && "job" in v && typeof v.job === "object";
}

/** Descarga un attachment autenticado (export xlsx/csv, reporte de errores). */
export async function downloadIoFile(path: string, fallbackName: string): Promise<void> {
  const res = await ioFetchRaw(path);
  if (!res.ok) {
    throw new Error(await extractError(res));
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const name = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  // Revocación diferida: dar tiempo al navegador a iniciar la descarga.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Construye la URL de export con formato + filtros como query params. */
export function buildExportPath(
  entityKey: string,
  format: "xlsx" | "csv",
  orgId: string | undefined,
  filters: Record<string, string | undefined>,
): string {
  const params = new URLSearchParams({ format });
  if (orgId) {
    params.set("orgId", orgId);
  }
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== "") {
      params.set(k, v);
    }
  }
  return `/api/io/export/${encodeURIComponent(entityKey)}?${params.toString()}`;
}

/** Construye la URL de un workbook multi-hoja (xlsx) con filtros como query. */
export function buildWorkbookPath(
  workbookKey: string,
  orgId: string | undefined,
  filters: Record<string, string | undefined>,
): string {
  const params = new URLSearchParams();
  if (orgId) {
    params.set("orgId", orgId);
  }
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== "") {
      params.set(k, v);
    }
  }
  const qs = params.toString();
  return `/api/io/export/workbook/${encodeURIComponent(workbookKey)}${qs ? `?${qs}` : ""}`;
}

async function extractError(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === "object" && "error" in body) {
      const err = (body as { error: unknown }).error;
      if (typeof err === "string") {
        return err;
      }
      if (err && typeof err === "object" && "message" in err) {
        return String((err as { message: unknown }).message);
      }
    }
  } catch {
    // cuerpo no-JSON — cae al genérico
  }
  return `HTTP ${res.status}`;
}
